import qrcode from 'qrcode-generator'

/**
 * Masa karekodu şablonları (QR Sipariş, 30 Eki 2026). Yazdırma ayrı bir
 * pencerede, kendi sayfası olarak: uygulamanın fiş yazdırma kuralları
 * (@media print) A4'e karışmasın. Ayarlardaki önizleme de aynı HTML'i
 * çiziyor; ekranda görülen kağıda çıkan.
 */

export type QrTemplate = 'tent' | 'sticker' | 'card'

export interface QrPrintItem {
  /** Masa adı ("Masa 5"). */
  name: string
  /** Salon adı, varsa. */
  hall?: string
  url: string
}

export interface QrPrintBrand {
  business: string
  /** Menünün vurgu rengi (#rrggbb). */
  accent: string
}

export const QR_TEMPLATES: { id: QrTemplate; name: string; hint: string; perPage: number }[] = [
  { id: 'tent', name: 'Masa çadırı', hint: 'Ortadan katlanır, masada dik durur; iki yüzünde de kod', perPage: 2 },
  { id: 'sticker', name: 'Kare etiket', hint: 'Masaya yapıştırmalık sade kare, 6,5 cm', perPage: 9 },
  { id: 'card', name: 'Renkli kart', hint: 'Menü renginde başlıklı büyük kart, A6', perPage: 4 },
]

function qrSvg(value: string): string {
  const qr = qrcode(0, 'M')
  qr.addData(value)
  qr.make()
  const n = qr.getModuleCount()
  let d = ''
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`
  return `<svg class="qr" viewBox="-1 -1 ${n + 2} ${n + 2}" shape-rendering="crispEdges" xmlns="http://www.w3.org/2000/svg"><rect x="-1" y="-1" width="${n + 2}" height="${n + 2}" fill="#fff"/><path d="${d}" fill="#111"/></svg>`
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)

// Tabler simgeleri (outline), uygulamadakilerle aynı.
const ICON = {
  bag: '<path d="M6.331 8h11.339a2 2 0 0 1 1.977 2.304l-1.255 8.152a3 3 0 0 1 -2.966 2.544h-6.852a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304"/><path d="M9 11v-5a3 3 0 0 1 6 0v5"/>',
  bell: '<path d="M10 5a2 2 0 0 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/><path d="M21 6.727a11.05 11.05 0 0 0 -2.794 -3.727"/><path d="M3 6.727a11.05 11.05 0 0 1 2.792 -3.727"/>',
  bill: '<path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2"/><path d="M9 7h6"/><path d="M9 11h6"/><path d="M13 15h2"/>',
}
const icon = (k: keyof typeof ICON) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">${ICON[k]}</svg>`

const actions = () => `
  <ul class="acts">
    <li>${icon('bag')}<span>Sipariş ver</span></li>
    <li>${icon('bell')}<span>Garson çağır</span></li>
    <li>${icon('bill')}<span>Hesap iste</span></li>
  </ul>`

function face(it: QrPrintItem, b: QrPrintBrand) {
  return `
    <div class="face">
      <div class="face__qr">${qrSvg(it.url)}</div>
      <div class="face__text">
        <p class="biz">${esc(b.business)}</p>
        <h2>${esc(it.name)}${it.hall ? `<small>${esc(it.hall)}</small>` : ''}</h2>
        <p class="lead">Telefonunuzun kamerasıyla okutun</p>
        ${actions()}
      </div>
    </div>`
}

function cardHtml(t: QrTemplate, it: QrPrintItem, b: QrPrintBrand): string {
  if (t === 'tent') {
    // Üst yarı ters: katlanınca iki yüz de dik okunuyor.
    return `<div class="tent"><div class="tent__half tent__half--flip">${face(it, b)}</div><div class="tent__half">${face(it, b)}</div></div>`
  }
  if (t === 'sticker') {
    return `
      <div class="stk">
        <p class="stk__name">${esc(it.name)}${it.hall ? ` <small>${esc(it.hall)}</small>` : ''}</p>
        <div class="stk__qr">${qrSvg(it.url)}</div>
        <p class="stk__cta">Okutun, sipariş verin</p>
      </div>`
  }
  return `
    <div class="crd">
      <header class="crd__head">
        <p>${esc(b.business)}</p>
        <span>Masadan sipariş</span>
      </header>
      <div class="crd__body">
        <h2>${esc(it.name)}${it.hall ? `<small>${esc(it.hall)}</small>` : ''}</h2>
        <div class="crd__qr">${qrSvg(it.url)}</div>
        <p class="lead">Telefonunuzun kamerasıyla okutun</p>
        ${actions()}
      </div>
    </div>`
}

function css(t: QrTemplate, b: QrPrintBrand, origin: string) {
  return `
  @font-face { font-family: 'Cal Sans'; font-weight: 100 900; src: url('${origin}/fonts/calsans-latin.woff2') format('woff2'); }
  @font-face { font-family: 'Cal Sans'; font-weight: 100 900; src: url('${origin}/fonts/calsans-latin-ext.woff2') format('woff2');
    unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF; }
  * { box-sizing: border-box; }
  html, body { margin: 0; }
  body { font-family: 'Cal Sans', Inter, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #141210;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; --a: ${b.accent}; }
  h2, p, ul { margin: 0; }
  .qr { display: block; width: 100%; height: auto; }
  .acts { display: flex; gap: 3mm; padding: 0; list-style: none; }
  .acts li { display: flex; flex-direction: column; align-items: center; gap: 1mm; font-size: 2.6mm; font-weight: 600; color: #3a3631; }
  .acts svg { width: 6mm; height: 6mm; padding: 1.4mm; border-radius: 50%; color: var(--a);
    background: color-mix(in srgb, var(--a) 14%, #fff); box-sizing: content-box; }
  .lead { font-size: 3mm; color: #6f6a63; }
  h2 small { display: block; margin-top: 0.8mm; font-size: 0.42em; font-weight: 600; color: #6f6a63; letter-spacing: 0.04em; }

  /* Masa çadırı: 200 × 140 mm, ortadan katlanıyor. */
  .tent { width: 200mm; height: 140mm; display: flex; flex-direction: column; border: 0.3mm dashed #c9c3bb; }
  .tent__half { height: 70mm; display: flex; }
  .tent__half + .tent__half { border-top: 0.3mm dashed #c9c3bb; }
  .tent__half--flip { transform: rotate(180deg); }
  .face { flex: 1; display: flex; align-items: center; gap: 8mm; padding: 7mm 10mm; }
  .face__qr { width: 52mm; flex: none; padding: 2mm; border-radius: 3mm; background: #fff;
    box-shadow: 0 0 0 0.6mm color-mix(in srgb, var(--a) 30%, #fff); }
  .face__text { display: flex; flex-direction: column; gap: 3mm; min-width: 0; }
  .face .biz { font-size: 3.2mm; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--a); }
  .face h2 { font-size: 12mm; line-height: 1; }

  /* Kare etiket: 65 × 65 mm. */
  .stk { width: 65mm; height: 65mm; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2mm;
    padding: 4mm; border: 0.3mm dashed #c9c3bb; }
  .stk__name { font-size: 5mm; font-weight: 700; }
  .stk__name small { font-size: 3mm; font-weight: 600; color: #6f6a63; }
  .stk__qr { width: 42mm; }
  .stk__cta { font-size: 2.8mm; font-weight: 600; color: var(--a); letter-spacing: 0.02em; }

  /* Renkli kart: A6, 100 × 140 mm. */
  .crd { width: 100mm; height: 140mm; display: flex; flex-direction: column; overflow: hidden; border-radius: 4mm;
    border: 0.3mm solid color-mix(in srgb, var(--a) 40%, #fff); background: #fff; }
  .crd__head { padding: 6mm 7mm 5mm; color: #fff; text-align: center;
    background: linear-gradient(135deg, var(--a), color-mix(in srgb, var(--a) 72%, #000)); }
  .crd__head p { font-size: 6mm; font-weight: 700; line-height: 1.1; }
  .crd__head span { display: block; margin-top: 1.2mm; font-size: 2.8mm; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; opacity: 0.85; }
  .crd__body { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3.5mm; padding: 5mm 7mm 7mm; text-align: center; }
  .crd h2 { font-size: 9mm; line-height: 1; }
  .crd__qr { width: 58mm; padding: 2mm; border-radius: 3mm; box-shadow: 0 0 0 0.6mm color-mix(in srgb, var(--a) 25%, #fff); }

  /* Sayfa düzeni. */
  .sheet { display: grid; justify-content: center; align-content: start; }
  .sheet--tent { grid-template-columns: 200mm; gap: 6mm; padding-top: 4mm; }
  .sheet--sticker { grid-template-columns: repeat(3, 65mm); gap: 0; padding-top: 6mm; }
  .sheet--card { grid-template-columns: repeat(2, 100mm); gap: 4mm; padding-top: 4mm; }
  .page { break-after: page; }
  .page:last-child { break-after: auto; }
  @page { size: A4 portrait; margin: 6mm; }
  @media screen { body.print { background: #e9e6e1; padding: 8mm 0; }
    body.print .page { width: 210mm; min-height: 297mm; margin: 0 auto 8mm; background: #fff; box-shadow: 0 2mm 8mm rgb(0 0 0 / 0.15); padding: 6mm 0; } }
  @media print { body.print .page { padding: 0; } }
  /* Önizleme: tek kart, ortada. */
  body.preview { display: grid; place-items: center; min-height: 100vh; background: transparent; }
  body.preview .crd, body.preview .tent, body.preview .stk { background: #fff; }
  `
}

function doc(body: string, cls: string, t: QrTemplate, b: QrPrintBrand, title: string) {
  const origin = window.location.origin
  return `<!doctype html><html lang="tr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${esc(title)}</title><style>${css(t, b, origin)}</style></head><body class="${cls}">${body}</body></html>`
}

/** Ayarlardaki önizleme: tek masa. */
export function qrPreviewHtml(t: QrTemplate, it: QrPrintItem, b: QrPrintBrand): string {
  return doc(cardHtml(t, it, b), 'preview', t, b, it.name)
}

/** Kartın milimetre ölçüsü (önizlemeyi ölçeklemek için). */
export const QR_CARD_MM: Record<QrTemplate, { w: number; h: number }> = {
  tent: { w: 200, h: 140 },
  sticker: { w: 65, h: 65 },
  card: { w: 100, h: 140 },
}

/** Yazdırma penceresi: A4 sayfalar, sayfa başına şablonun sığdığı kadar. */
export function qrPrintHtml(t: QrTemplate, items: QrPrintItem[], b: QrPrintBrand): string {
  const per = QR_TEMPLATES.find((x) => x.id === t)!.perPage
  const pages: string[] = []
  for (let i = 0; i < items.length; i += per) {
    pages.push(
      `<section class="page"><div class="sheet sheet--${t}">${items
        .slice(i, i + per)
        .map((it) => cardHtml(t, it, b))
        .join('')}</div></section>`,
    )
  }
  const script = `<script>document.fonts.ready.then(function(){setTimeout(function(){window.print()},300)})</script>`
  return doc(pages.join('') + script, 'print', t, b, `${b.business} masa karekodları`)
}
